import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Language as LanguageIcon } from '@mui/icons-material';
import { useTenant } from '../providers/TenantProvider';
import { useCustomerOnboarding } from '../hooks/useCustomerOnboarding';
import type { OnboardingStatus } from '../lib/db/types';

const statusColors: Record<OnboardingStatus, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
  draft: 'default',
  pending: 'warning',
  completed: 'success',
};

const statusLabels: Record<OnboardingStatus, string> = {
  draft: 'Draft',
  pending: 'Pending Review',
  completed: 'Completed',
};

export const WebsiteDashboardPage: React.ComponentType = () => {
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const { onboarding, loading, error } = useCustomerOnboarding(tenantId || '');

  const handleStartOnboarding = () => {
    navigate('/website/onboarding');
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="lg">
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <LanguageIcon color="primary" sx={{ fontSize: 40 }} />
            <Typography variant="h4" component="h1">
              Website Management
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage your website content and onboarding process
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error.message}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Onboarding Status Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Website Onboarding
                </Typography>
                
                {onboarding ? (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Current Status
                      </Typography>
                      <Chip
                        label={statusLabels[onboarding.status]}
                        color={statusColors[onboarding.status]}
                        size="small"
                      />
                    </Box>

                    {onboarding.data?.name && (
                      <Typography variant="body2" color="text.secondary">
                        <Box component="strong" sx={{ fontWeight: 700 }}>Site Name:</Box> {onboarding.data.name}
                      </Typography>
                    )}
                    
                    {onboarding.updatedAt && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        <Box component="strong" sx={{ fontWeight: 700 }}>Last Updated:</Box>{' '}
                        {onboarding.updatedAt.toDate().toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    You haven't started the website onboarding process yet. Complete the form to get your website up and running.
                  </Typography>
                )}
              </CardContent>
              <CardActions>
                <Button
                  variant="contained"
                  onClick={handleStartOnboarding}
                  fullWidth
                >
                  {onboarding ? 'Continue Onboarding' : 'Start Onboarding'}
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* Quick Info Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  What's Next?
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  The onboarding process will collect information about your gym including:
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Business details and contact information
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Location and address
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Coaches and staff profiles
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Class schedule and pricing plans
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    FAQs and business hours
                  </Typography>
                </Box>
              </CardContent>
              <CardActions>
                <Button
                  variant="outlined"
                  onClick={handleStartOnboarding}
                  fullWidth
                >
                  Go to Onboarding Form
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* Help/Info Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, bgcolor: 'info.light' }}>
              <Typography variant="h6" gutterBottom>
                Need Help?
              </Typography>
              <Typography variant="body2">
                The website onboarding form will guide you through setting up your gym's
                website. Once submitted, our team will review your information and
                create your custom website.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <Box component="strong" sx={{ fontWeight: 700 }}>Status Definitions:</Box>
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0, mt: 1 }}>
                <Typography component="li" variant="body2">
                  <Box component="strong" sx={{ fontWeight: 700 }}>Draft</Box> - You're still working on the form
                </Typography>
                <Typography component="li" variant="body2">
                  <Box component="strong" sx={{ fontWeight: 700 }}>Pending Review</Box> - Submitted and awaiting team review
                </Typography>
                <Typography component="li" variant="body2">
                  <Box component="strong" sx={{ fontWeight: 700 }}>Completed</Box> - Website has been created and is live
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default WebsiteDashboardPage;
